import type { IntensityTarget, SportType, ZoneDefinition } from '@/types';

/**
 * Build a short, deterministic preamble that pins the AI to the user's CURRENT zone table
 * and intensity target. We prepend this to the user's free-form prompt so when they say
 * "S2" or "Tempo" the model uses their (possibly edited) zone bounds — not the static
 * Coggan/Friel defaults baked into the system prompt.
 *
 * The format is intentionally compact: a one-line target line plus a tab-delimited table
 * the model can copy values from verbatim. Avoid Markdown decoration so the token cost stays
 * predictable across PL/EN.
 */
export function buildZoneContextPreamble(
  sport: SportType,
  intensityTarget: IntensityTarget,
  zones: readonly ZoneDefinition[],
  language: 'en' | 'pl',
): string {
  if (!Array.isArray(zones) || zones.length === 0) return '';

  const unit = unitLabel(sport, intensityTarget, language);
  const header =
    language === 'pl'
      ? `KONTEKST STREF UŻYTKOWNIKA — Sport: ${sport === 'bike' ? 'rower' : 'bieg'}, cel intensywności: ${targetLabelPl(intensityTarget)} (${unit}).`
      : `USER ZONE CONTEXT — Sport: ${sport}, intensity target: ${targetLabelEn(intensityTarget)} (${unit}).`;

  const rowsHeader =
    language === 'pl' ? 'ID\tNAZWA\tDOLNY\tGÓRNY' : 'ID\tNAME\tLOW\tHIGH';
  const rows = zones
    .map((z) => `${z.id}\t${z.name}\t${pct(z.min)}\t${pct(z.max)}`)
    .join('\n');

  const usage =
    language === 'pl'
      ? 'Gdy użytkownik wymienia strefę (np. "Z3", "S2", "Tempo"), użyj DOKŁADNIE średniej z tej tabeli (np. dla S2: (DOLNY+GÓRNY)/2). Wartości intensywności w polu "intensity" pozostaw jako % (np. "70%") lub jako ID strefy (np. "Z3").'
      : 'When the user names a zone (e.g. "Z3", "S2", "Tempo"), use EXACTLY the midpoint of the row in this table (e.g. for S2: (LOW+HIGH)/2). Keep "intensity" values as a % (e.g. "70%") or as a zone id (e.g. "Z3").';

  return `${header}\n${rowsHeader}\n${rows}\n${usage}\n\n`;
}

function pct(v: number): string {
  return `${Math.round(v * 100)}%`;
}

function unitLabel(sport: SportType, target: IntensityTarget, language: 'en' | 'pl'): string {
  if (sport === 'bike') {
    if (target === 'hr') return language === 'pl' ? '% LTHR' : '% LTHR';
    return language === 'pl' ? '% FTP' : '% FTP';
  }
  if (target === 'pace')  return language === 'pl' ? '% tempa progowego' : '% threshold pace';
  if (target === 'hr')    return language === 'pl' ? '% LTHR' : '% LTHR';
  return language === 'pl' ? '% mocy progowej' : '% threshold power';
}

function targetLabelPl(target: IntensityTarget): string {
  if (target === 'pace') return 'Tempo';
  if (target === 'hr')   return 'Tętno';
  return 'Moc';
}

function targetLabelEn(target: IntensityTarget): string {
  if (target === 'pace') return 'Pace';
  if (target === 'hr')   return 'Heart Rate';
  return 'Power';
}
